<template>
  <!-- Removed the redundant v-navigation-drawer wrapper -->
  <!-- Menu content is now rendered directly inside the parent navigation drawer -->
  <div
    class="navigation-rail-content"
    data-testid="nav-drawer-container"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Pin toggle button at the top -->
    <div v-if="showPinButton" class="pin-container">
      <v-btn
        icon
        size="small"
        variant="text"
        color="on-selectable"
        class="pin-button"
        :class="{ 'rail-pin': isRailMode }"
        :title="isPinned ? 'Unpin menu' : 'Pin menu'"
        data-testid="nav-pin-toggle-button"
        @click="togglePinned"
      >
        <v-icon
          :icon="isPinned ? 'mdi-pin' : 'mdi-pin-off'"
          size="1.25rem"
          :style="!isPinned ? 'transform: rotate(180deg); transition: transform 0.2s ease' : 'transition: transform 0.2s ease'"
        />
      </v-btn>
    </div>

    <v-divider/>
    
    <v-list class="navigation-list" :class="{ 'rail-mode': isRailMode }">
      <template v-for="item in menuItems" :key="item.id">
        <v-divider 
          v-if="item.id === 'public-separator' || item.id === 'account-separator'" 
          class="my-2 separator-divider"
          :class="{ 'rail-divider': isRailMode }"
        />
        <v-list-item
          v-else
          class="pa-1"
        >
          <v-btn
            :variant="isActive(item.path) ? 'flat' : 'text'"
            :color="isActive(item.path) ? 'selectable' : undefined"
            class="navigation-button"
            :class="{ 'rail-button': isRailMode, 'active-button': isActive(item.path) }"
            :data-testid="`nav-menu-${item.id}-link`"
            @click="handleItemClick(item)"
          >
            <v-icon 
              :icon="item.icon" 
              size="1.5rem"
              class="button-icon"
            />
            <span 
              class="button-text"
              :class="{ 'rail-text': isRailMode }"
            >
              {{ item.title }}
            </span>
          </v-btn>
        </v-list-item>
      </template>
    </v-list>
    
    <!-- INDA Logo as background -->
    <div v-show="true" class="logo-background">
      <img src="~/assets/images/INDA.svg" alt="" class="background-logo">
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth } from '~/composables/useAuth';
import { useRoleAccess } from '~/composables/useRoleAccess';
import { useAppTheme } from '~/composables/useTheme';
import { UserRole } from '#shared/enums';
import { useDisplay } from 'vuetify';

// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  isRail: {
    type: Boolean,
    default: false
  },
  isPinned: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['update:is-open', 'menu-item-click', 'click-outside', 'update:is-pinned']);

// Display composable for responsive behavior
const display = useDisplay();
const mdAndUp = computed(() => display.mdAndUp.value);

// Pin state - uses prop for two-way binding with parent
const isPinned = computed(() => props.isPinned);

// Show pin button only on md and above
const showPinButton = computed(() => mdAndUp.value);

// Hover state for rail expansion
const isHovered = ref(false);

// Computed property to determine if rail mode should be active
// When hovering or pinned, the rail should be expanded
const isRailMode = computed(() => {
  return props.isRail && !isHovered.value;
});

// Hover handlers
const handleMouseEnter = () => {
  if (props.isRail) {
    isHovered.value = true;
  }
};

const handleMouseLeave = () => {
  if (props.isRail) {
    isHovered.value = false;
  }
};

// Toggle pinned state
const togglePinned = () => {
  emit('update:is-pinned', !isPinned.value);
};

// Route for active state checking
const route = useRoute();

// Role access control
const roleAccess = useRoleAccess();

// Theme control for logout
const { revertToSystemTheme } = useAppTheme();

// All menu items
const allMenuItems = ref([
  { id: 'home', title: 'Home', icon: 'mdi-home', path: '/' },
  { id: 'assessments', title: 'Assessments', icon: 'mdi-clipboard-text', path: '/assessments' },
  { id: 'reports', title: 'Reports', icon: 'mdi-chart-box-outline', path: '/reports' },
  { id: 'centre', title: 'Centre', icon: 'mdi-office-building', path: '/centre' },
  { id: 'sessions', title: 'Sessions', icon: 'mdi-calendar-clock', path: '/sessions' },
  { id: 'drafts', title: 'Drafts', icon: 'mdi-file-document-edit-outline', path: '/drafts' },
  { id: 'memos', title: 'Memos', icon: 'mdi-note', path: '/memos' },
  { id: 'report', title: 'Report', icon: 'mdi-chart-bar', path: '/report' },
  { id: 'statistics', title: 'Statistics', icon: 'mdi-chart-pie', path: '/statistics' },
  { id: 'administration', title: 'Administration', icon: 'mdi-account-multiple', path: '/administration' },
  { id: 'public-separator', title: 'separator', icon: 'divider', path: '' },
  { id: 'faqs', title: 'FAQ', icon: 'mdi-help-circle-outline', path: '/faqs' },
  { id: 'legal', title: 'Terms & Privacy', icon: 'mdi-shield-lock-outline', path: '/legal' },
  { id: 'contact', title: 'Contact', icon: 'mdi-email-outline', path: '/contact' },
  { id: 'account-separator', title: 'separator', icon: 'divider', path: '' },
  { id: 'account', title: 'Account', icon: 'mdi-account-circle', path: '/account' },
  { id: 'logout', title: 'Log out', icon: 'mdi-logout', path: '/logout' },
]);

// Filtered menu items based on role
const menuItems = computed(() => {
  
  return allMenuItems.value.filter(item => {
    // Home page should only appear for non-authenticated users
    if (item.id === 'home') {
      return roleAccess.getCurrentRole() === null;
    }
    
    // Define public page IDs (informational pages that are not core app functions)
    const publicPageIds = ['faqs', 'legal', 'contact'];
    
    // Students should not see public pages in the navigation rail - they should focus on assessments
    if (publicPageIds.includes(item.id) && roleAccess.getCurrentRole() === UserRole.STUDENT) {
      return false;
    }
    
    // All other roles should always include the public pages.
    if (publicPageIds.includes(item.id)) {
      return true;
    }

    // These items should always be displayed to all logged in users
    const privatePageIds = ['account', 'public-separator', 'account-separator', 'logout'];
    if (roleAccess.getCurrentRole() && privatePageIds.includes(item.id)) {
      return true;
    }
    
    // Role-based filtering for protected pages
    if (item.id === 'administration') {
      return roleAccess.canAccessAdministration();
    }
    if (item.id === 'centre') {
      return roleAccess.canAccessCentre();
    }
    if (item.id === 'sessions') {
      return roleAccess.canAccessSessions();
    }
    if (item.id === 'statistics') {
      return roleAccess.canAccessStatistics();
    }
    if (item.id === 'drafts') {
      return roleAccess.canAccessDrafts();
    }
    if (item.id === 'report') {
      return roleAccess.canAccessReport();
    }
    if (item.id === 'assessments') {
      return roleAccess.canAccessAssessment();
    }
    if (item.id === 'reports') {
      return roleAccess.canAccessReports();
    }
    if (item.id === 'memos') {
      return roleAccess.canAccessMemos();
    }
    return false;
  });
});

// No need for two-way binding as the parent navigation drawer now handles this

// Check if a menu item is active
const isActive = (path: string): boolean => {
  return route.path === path;
};

// Handle menu item click
interface MenuItem {
  id: string;
  title: string;
  icon: string;
  path: string;
}

const handleItemClick = async (item: MenuItem) => {
  emit('menu-item-click', item);
  
  // Handle menu item click
  if (item.id === 'logout') {
    const auth = useAuth();
    await auth.logout();
    // Revert to system theme after logout
    revertToSystemTheme();
    // No need to manually push to /login as logout() already does this
  } else if (route.path !== item.path) {
    // Only navigate if we're not already on this page
    // This prevents unnecessary navigation and title changes
    navigateTo(item.path);
  }
  // If already on the same page, do nothing

  // If on a narrow screen (not rail mode) and not pinned, close the menu after item click
  if (!props.isRail && !isPinned.value) {
    emit('update:is-open', false);
  }
};
</script>

<style scoped>
.navigation-rail-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.pin-container {
  display: flex;
  justify-content: flex-end;
  padding: 8px 8px 0 8px;
  min-height: 40px;
}

.logo-background {
  position: absolute;
  bottom: 55px;
  left: 55px;
  pointer-events: none;
  z-index: 0;
}

.background-logo {
  width: 130px;
  height: 130px;
  object-fit: contain;
  opacity: 0.25;
}

.pin-button {
  transition: transform 0.2s ease;
}

.pin-button:hover {
  transform: scale(1.1);
}

.rail-pin {
  margin-left: 0;
}

.navigation-list {
  padding: 8px 0;
  position: relative;
  z-index: 1;
}

.navigation-list.rail-mode {
  padding: 8px 0;
  align-items: center;
}

.navigation-button {
  width: 100%;
  min-height: 48px !important;
  height: 48px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
  padding: 0 12px !important;
  overflow: hidden;
}

.rail-button {
  justify-content: flex-start !important;
  padding: 0 12px !important;
  min-width: 48px !important;
  width: 48px !important;
}

.button-icon {
  min-width: 24px;
  width: 24px;
  flex-shrink: 0;
  /* Icon always stays in the same position */
  margin-left: 0;
  margin-right: 0;
}

.button-text {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  margin-left: 12px;
  /* No transition to prevent jumping */
}

/* When rail mode, text is clipped by button width */
/* No opacity change, just clipped by overflow */

/* Divider styling - keep consistent vertical space */
.separator-divider {
  margin: 8px 16px;
}

.rail-divider {
  width: 24px;
  margin: 8px auto;
}

/* Ensure list items maintain consistent spacing */
.v-list-item {
  padding: 4px !important;  /* Equal spacing all around each item */
  min-height: auto;
}

/* Override global outline for selected NavigationRail buttons */
.navigation-button.active-button {
  outline: none !important;
  box-shadow: none !important;
}

/* Ensure proper text color for unselected buttons (text variant) */
.navigation-button:not(.active-button) {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.navigation-button:not(.active-button) .v-icon {
  color: inherit !important;
}

/* Ensure proper text color for selectable buttons - use on-selected for active items */
.navigation-button[color="selectable"],
.navigation-button.bg-selectable,
.navigation-button.active-button {
  color: rgb(var(--v-theme-on-selected)) !important;
}

.navigation-button[color="selectable"] .v-icon,
.navigation-button.bg-selectable .v-icon,
.navigation-button.active-button .v-icon {
  color: inherit !important;
}
</style>

